import { action, makeObservable, observable } from "mobx"
import { ErrorHandler } from "../../error/ErrorHandler"
import { TableConfig } from "../../components/DataTable/types/TableConfig"
import { ViewModel } from "../ViewModel"
import { FileService } from "../../../domain/services/FileService"
import { GetInvoicesUseCase } from "../../../domain/useCases/billing/getInvoices"
import { Invoice } from "../../../domain/entities/Invoice"
import { InvoicesFilter } from "../../../domain/types/billing/InvoicesFilter"
import { InvoiceStatus } from "../../../domain/enum/invoiceStatus.enum"
import { UpdateTransactionCreatedDateUseCase } from "../../../domain/useCases/billing/updateTransactions"
import { UpdateInvoiceUseCase } from "../../../domain/useCases/billing/updateInvoice"
import { ExportInvoicePaymentsUseCase } from "../../../domain/useCases/billing/exportInvoicePayments"

interface Props {
	ErrorHandler: ErrorHandler
	GetInvoicesUseCase: GetInvoicesUseCase
	ExportInvoicePaymentsUseCase: ExportInvoicePaymentsUseCase
	UpdateInvoiceUseCase: UpdateInvoiceUseCase
	UpdateTransactionCreatedDateUseCase: UpdateTransactionCreatedDateUseCase
}
type ErrorType = "header" | "detail" | undefined
type Error = { message: string; type: ErrorType }

export class PaymentsViewModel extends ViewModel {
	private _errorHandler
	private _getInvoicesUseCase: GetInvoicesUseCase
	private _exportInvoicePaymentsUseCase: ExportInvoicePaymentsUseCase
	private _updateInvoiceUseCase: UpdateInvoiceUseCase

	isFetching: boolean = false
	limit = 400
	isLoading: boolean = false
	error: Error = { message: "", type: undefined }
	editMode: boolean = false
	invoices: Invoice[] = []
	selectedInvoiceIds: number[] = []
	seletectInvoicesTransactionsIds: number[] = []
	selectedInvoice: Invoice | null = null
	selectedStatus: InvoiceStatus = InvoiceStatus.EMITTED
	tableConfig: TableConfig = {
		pageSize: 20,
		sort: { order: "descend", field: "id" }
	}
	tipTableFilters = {}
	invoiceNumberStart: number | null = null

	constructor({ ErrorHandler, GetInvoicesUseCase, ExportInvoicePaymentsUseCase, UpdateInvoiceUseCase }: Props) {
		super()
		makeObservable(this, {
			isFetching: observable,
			isLoading: observable,
			invoices: observable,
			selectedInvoiceIds: observable,
			selectedInvoice: observable,
			seletectInvoicesTransactionsIds: observable,
			selectedStatus: observable,
			invoiceNumberStart: observable,
			setInvoices: action,
			setLoading: action,
			setIsFetching: action,
			setSelectedInvoiceIds: action,
			setSelectedInvoice: action,
			setSeletectInvoicesTransactionsIds: action,
			cleanPagination: action,
			setSelectedStatus: action,
			setInvoiceNumberStart: action
		})
		this._errorHandler = ErrorHandler
		this._getInvoicesUseCase = GetInvoicesUseCase
		this._exportInvoicePaymentsUseCase = ExportInvoicePaymentsUseCase
		this._updateInvoiceUseCase = UpdateInvoiceUseCase
		this.fetchInvoices()
	}

	public async fetchInvoices(filter?: InvoicesFilter) {
		try {
			this.setIsFetching(true)
			const invoices = await this._getInvoicesUseCase.exec({
				offset: 0,
				limit: this.limit,
				...filter
			})
			const selectedInvoice = invoices.find(i => i.id === this.selectedInvoice?.id) ?? null
			this.setSelectedInvoice(selectedInvoice)
			this.setSelectedInvoiceIds([])
			this.setInvoices(invoices)
			this.cleanPagination()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setIsFetching(false)
		}
	}

	public async exportInvoicePayments(filter?: InvoicesFilter) {
		try {
			this.setLoading(true)
			const file = await this._exportInvoicePaymentsUseCase.exec({
				offset: 0,
				limit: this.limit,
				...filter
			})
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `Pagos_y_Cobros_${dateString}.csv`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateInvoiceDate(
		invoiceId: number,
		data: { sellerPaymentDate?: string | null; sellerCollectionDate?: string | null }
	) {
		try {
			const invoice = this.invoices.find(i => i.id === invoiceId)
			const sellerCollectionDate = data.sellerCollectionDate
				? data.sellerCollectionDate
				: !invoice?.seller?.feeException && !invoice?.sellerCollectionDate
				? data.sellerPaymentDate
				: null
			console.log(data)
			this.setInvoices(
				this.invoices.map(i =>
					i.id === invoiceId
						? {
								...i,
								sellerPaymentDate: data.sellerPaymentDate,
								sellerCollectionDate: sellerCollectionDate
						  }
						: i
				)
			)
			this.setLoading(true)
			await this._updateInvoiceUseCase.exec({ invoiceId: [invoiceId], ...data })
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public cleanPagination() {
		this.pagination = 0
		this.setHasNextPage(true)
	}

	// ACTIONS
	setInvoices(invoices: Invoice[]) {
		this.invoices = invoices
	}

	setSelectedInvoiceIds(selectedInvoiceIds: number[]) {
		this.selectedInvoiceIds = selectedInvoiceIds
	}

	setSeletectInvoicesTransactionsIds(seletectInvoicesTransactionsIds: number[]) {
		this.seletectInvoicesTransactionsIds = seletectInvoicesTransactionsIds
	}

	setSelectedInvoice(invoice: Invoice | null) {
		this.selectedInvoice = invoice
	}

	setSelectedStatus(selectedStatus: InvoiceStatus) {
		this.selectedStatus = selectedStatus
	}

	setLoading(isLoading: boolean) {
		this.isLoading = isLoading
	}

	setIsFetching(isFetching: boolean) {
		this.isFetching = isFetching
	}

	setError(message: string, type: ErrorType) {
		this.error = { message, type }
	}

	setTableConfig(config: Partial<typeof this.tableConfig>) {
		this.tableConfig = { ...this.tableConfig, ...config }
	}

	setInvoiceNumberStart(invoiceNumberStart: number | null) {
		this.invoiceNumberStart = invoiceNumberStart
	}
}
